import * as Sentry from '@sentry/react'
import { useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useParams, useHistory } from 'react-router-dom'
import { cloneDeep } from 'lodash'
import { Button, Loader, PageTopBar, PageView } from '../../components'
import { UserContext } from '../../hooks/UserContext'
import { fetchStore } from '../../services'
import { useQuery, useQueryClient } from 'react-query'
import { useTranslation } from 'react-i18next'
import { BILLING_STORE_CONFIGS, BILLING_STORE_CONFIG_DUPLICATE } from '../../navigation/constants'
import { Link } from 'react-router-dom/cjs/react-router-dom.min'
import { LeftOutlined } from '@ant-design/icons'
import { CheckCircleIcon, ExclamationCircleIcon } from '@heroicons/react/outline'
import { BillingConfigTab } from './BillingConfigTab'
import { fetchStoreBillingServices, fetchStoreShippingPackages, createStoreBillingConfiguration, fetchStoresWithBillingConfigurations } from '../../services'
import { fetchWarehouse, fetchWarehouseVolumeCategories } from '../../services/warehouseSevices'
import {
  CONFIG_CURRENCY_BY_COUNTRY,
  CONFIG_CUTOFF_MODES,
  NATIONAL_MX_DEPRECATED_SHIPPING_LABELS_SERVICE_TYPE_CATEGORIES,
} from '../../components/Billing/constants'
import {
  applyMinimumFeeDefault,
  dropMinimumFeeUnlessMonthly,
  fetchAllBillingServices,
  mapLoadedBillingServices,
  requiresChannelPricesDiscard,
  toApiBillingServices,
} from '../../components/Billing/utils'
import { Notification, ConfirmDialog } from '../../components'
import { DuplicateConfigFromStoreModal } from './DuplicateConfigFromStoreModal'

export const BillingStoreConfigTabs = () => {
  const { user } = useContext(UserContext)
  const { t, i18n } = useTranslation()
  const history = useHistory()
  const queryClient = useQueryClient()
  const { store_id, warehouse_id, billing_configuration_id } = useParams()
  const {
    isLoading: isLoadingWarehouse,
    isFetching: isFetchingWarehouse,
    error: errorWarehouse,
    data: warehouse,
  } = useQuery(['warehouse', warehouse_id], () => fetchWarehouse(warehouse_id))
  const { isLoading, isFetching, isError, error, data } = useQuery(['store', store_id], () => fetchStore(store_id))
  const [validForm, setValidForm] = useState('default')
  const [currentTab, setCurrentTab] = useState(0)
  const [notificationMessage, setNotificationMessage] = useState({ show: false, type: 'error', title: '' })
  const [tabOptions, setTabOptions] = useState([])
  const [tabStates, setTabStates] = useState([])
  const [storeBillingServices, setStoreBillingServices] = useState(null)
  const [storeShippingPackages, setStoreShippingPackages] = useState(null)
  const [warehouseLocationVolumeCategories, setWarehouseLocationVolumeCategories] = useState(null)
  const [storeBillingConfiguration, setStoreBillingConfiguration] = useState({
    cutoff_interval: null,
    cutoff_mode: CONFIG_CUTOFF_MODES.CUTOFF_MODE_END_OF_INTERVAL,
  })
  const handleSelectedItem = value => {
    setValidForm(value)
  }
  const [isLoadingData, setIsLoadingData] = useState(true)
  const baseValidStatuses = {
    billing_date: false,
    storage: false,
    pick_and_pack: false,
    tech_fee: false,
    shipping_label: false,
    international_shipping_label: false,
    replenishment: false,
    returns: false,
    work_orders: false,
    shipping_insurance: false,
  }
  const [tabsValidStatuses, setTabsValidStatuses] = useState(baseValidStatuses)
  const [showConfirmSaveDialog, setShowConfirmSaveDialog] = useState(false)
  const [showDiscardChannelPricesDialog, setShowDiscardChannelPricesDialog] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [showCopyFromOtherStoreDialog, setShowCopyFromOtherStoreDialog] = useState(false)
  const isConfigCreatedRef = useRef(false)

  const onContinue = async currentKey => {
    const currentIndex = tabOptions.findIndex(tab => tab.key === currentKey)
    const nextIndex = tabOptions.length > currentIndex + 1 ? currentIndex + 1 : currentIndex
    setTabOptions(prevOptions => {
      return prevOptions.map((tab, index) => {
        if (index === currentIndex) {
          tab.validated = true
        }
        if (index === nextIndex) {
          tab.disabled = false
        }
        return tab
      })
    })

    if (currentIndex === nextIndex) {
      setShowConfirmSaveDialog(true)
    }

    setCurrentTab(nextIndex)
  }

  useEffect(() => {
    const fetchData = async () => {
      setIsLoadingData(true)

      let defaultServices = null
      const isDuplicatingConfiguration = billing_configuration_id !== undefined

      if (isDuplicatingConfiguration) {
        defaultServices = await fetchAllBillingServices(fetchStoreBillingServices, {
          store_billing_configuration_id: billing_configuration_id,
          warehouse_id: warehouse_id,
        })
      } else {
        const fetchStoresWithBillingConfigurationsParams = { having_current_billing_configuration: true, warehouse_id: warehouse_id, per_page: 1 }
        const fetchStoresWithBillingConfigurationsResult = await fetchStoresWithBillingConfigurations(fetchStoresWithBillingConfigurationsParams)

        if (fetchStoresWithBillingConfigurationsResult.stores.length === 0) {
          defaultServices = []
        } else {
          defaultServices = await fetchAllBillingServices(fetchStoreBillingServices, {
            warehouse_id: warehouse_id,
            store_billing_configuration_id: fetchStoresWithBillingConfigurationsResult.stores[0].current_billing_configuration_with_warehouse_ids.id,
          })
        }
      }

      defaultServices = mapLoadedBillingServices(defaultServices, { storeId: store_id, warehouseId: warehouse_id, isDuplicating: isDuplicatingConfiguration })

      if (isDuplicatingConfiguration) {
        // The chosen configuration's minimum fee, or its absence, carries over as is. The wizard
        // can outlive a switch from creating to duplicating, so a weekly choice made meanwhile still wins.
        defaultServices = dropMinimumFeeUnlessMonthly(defaultServices, storeBillingConfiguration.cutoff_interval)
      } else {
        // The country decides the default minimum, and the warehouse query this page already runs may not have landed yet.
        // Without it the wizard still loads, just with no minimum fee preset: the copied one is dropped and billing can add it.
        let loadedWarehouse = null
        try {
          loadedWarehouse = await queryClient.fetchQuery(['warehouse', warehouse_id], () => fetchWarehouse(warehouse_id), { staleTime: 60 * 1000 })
        } catch (error) {
          Sentry.captureException(error)
        }
        defaultServices = applyMinimumFeeDefault(defaultServices, {
          currency: CONFIG_CURRENCY_BY_COUNTRY[loadedWarehouse?.country],
          cutoffInterval: storeBillingConfiguration.cutoff_interval,
          storeId: store_id,
          warehouseId: warehouse_id,
        })
      }

      const fetchStoreShippingPackagesParams = { warehouse_id: warehouse_id }
      const currentStoreShippingPackages = await fetchStoreShippingPackages(store_id, fetchStoreShippingPackagesParams)

      const currentWarehouseLocationVolumeCategories = await fetchWarehouseVolumeCategories(warehouse_id)

      setStoreBillingServices(defaultServices)
      setStoreShippingPackages(currentStoreShippingPackages)
      setWarehouseLocationVolumeCategories(currentWarehouseLocationVolumeCategories)

      setIsLoadingData(false)
    }
    fetchData()
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reload only when the route changes; the cutoff interval is read once, not a reason to refetch
  }, [warehouse_id, store_id, billing_configuration_id])

  const additionalServices = useMemo(
    () => data?.additional_billing_services || [],
    // eslint-disable-next-line react-hooks/exhaustive-deps -- stabilize reference: only re-compute when the serialized list actually changes
    [JSON.stringify(data?.additional_billing_services)],
  )

  useEffect(() => {
    resetTabOptions()

    if (warehouse?.country === 'BR') {
      setTabsValidStatuses(prevStatuses => ({
        ...prevStatuses,
        difal: false,
      }))
    } else {
      setTabsValidStatuses(baseValidStatuses)
    }

    if (additionalServices.includes('SHIPPING_LABEL_NATURA_VENDA_DIRETA')) {
      setTabsValidStatuses(prevStatuses => ({ ...prevStatuses, natura_shipping_label: false }))
    }
    if (additionalServices.includes('SHIPPING_LABEL_NATURA_VENDA_DIRETA_ADDITIONAL_LABEL')) {
      setTabsValidStatuses(prevStatuses => ({ ...prevStatuses, natura_additional_label: false }))
    }
    if (additionalServices.includes('PICK_AND_PACK_NATURA_VENDA_DIRETA')) {
      setTabsValidStatuses(prevStatuses => ({ ...prevStatuses, natura_pick_and_pack: false }))
    }

    setStoreBillingConfiguration(prevStatuses => ({
      ...prevStatuses,
      currency: CONFIG_CURRENCY_BY_COUNTRY[warehouse?.country],
    }))
  }, [warehouse, additionalServices])

  const resetTabOptions = () => {
    const options = []
    options.push(
      {
        key: 'billing_date',
        disabled: false,
        validated: false,
      },
      {
        key: 'storage',
        disabled: true,
        validated: false,
      },
      {
        key: 'pick_and_pack',
        disabled: true,
        validated: false,
      },
      {
        key: 'tech_fee',
        disabled: true,
        validated: false,
      },
      {
        key: 'shipping_label',
        disabled: true,
        validated: false,
      },
      {
        key: 'international_shipping_label',
        disabled: true,
        validated: false,
      },
      {
        key: 'replenishment',
        disabled: true,
        validated: false,
      },
      {
        key: 'returns',
        disabled: true,
        validated: false,
      },
      {
        key: 'work_orders',
        disabled: true,
        validated: false,
      },
      {
        key: 'shipping_insurance',
        disabled: true,
        validated: false,
      },
    )

    if (warehouse?.country === 'BR') {
      options.push({
        key: 'difal',
        disabled: true,
        validated: false,
      })
    }

    const additionalServices = data?.additional_billing_services || []
    if (additionalServices.includes('SHIPPING_LABEL_NATURA_VENDA_DIRETA')) {
      options.push({ key: 'natura_shipping_label', disabled: true, validated: false })
    }
    if (additionalServices.includes('SHIPPING_LABEL_NATURA_VENDA_DIRETA_ADDITIONAL_LABEL')) {
      options.push({ key: 'natura_additional_label', disabled: true, validated: false })
    }
    if (additionalServices.includes('PICK_AND_PACK_NATURA_VENDA_DIRETA')) {
      options.push({ key: 'natura_pick_and_pack', disabled: true, validated: false })
    }

    setTabOptions(options)
  }

  useEffect(() => {
    const unblockPage = history.block(() => {
      const exitCurrentPage = isConfigCreatedRef.current ? true : window.confirm(t('billing.confirm_exit_dialog.title'))

      if (exitCurrentPage) {
        unblockPage()
        return true
      } else {
        return false
      }
    })
  }, [history, t])

  const renderTabTitle = (option, index) => {
    const isTabValid = tabsValidStatuses[option.key]

    const icon = option.disabled ? (
      <ExclamationCircleIcon className="h-4 w-4 text-blue-gray-400" />
    ) : isTabValid ? (
      <CheckCircleIcon className="h-4 w-4 text-green-400" />
    ) : (
      <ExclamationCircleIcon className="h-4 w-4 text-red-400" />
    )
    const textStyle = option.disabled ? 'text-gray-400' : currentTab === index ? 'text-blue-500' : ''

    return (
      <div className="flex items-center">
        {icon}
        <span className={`ml-2 text-base ${textStyle}`}>{t(`billing.tabTitles.${option.key}`)}</span>
      </div>
    )
  }

  const handleOnTabChange = (option, tabIndex) => {
    if (!option.disabled) {
      setCurrentTab(tabIndex)
    }
  }

  const handleOnIsValid = (configKey, isValid) => {
    const newTabsValidStatuses = { ...tabsValidStatuses }
    newTabsValidStatuses[configKey] = isValid
    setTabsValidStatuses(newTabsValidStatuses)
  }

  const filterDeprecatedCategories = storeBillingServices => {
    const DEPRECATED_CATEGORIES = Object.values(NATIONAL_MX_DEPRECATED_SHIPPING_LABELS_SERVICE_TYPE_CATEGORIES)

    return storeBillingServices.filter(config => {
      const { service_type_category } = config
      return !DEPRECATED_CATEGORIES.includes(service_type_category)
    })
  }

  const persistConfiguration = async (discardChannelPrices = false) => {
    const filteredStoreBillingServices = filterDeprecatedCategories(storeBillingServices)

    setIsSaving(true)

    try {
      await createStoreBillingConfiguration(
        store_id,
        warehouse_id,
        storeBillingConfiguration,
        toApiBillingServices(filteredStoreBillingServices),
        discardChannelPrices,
      )
      isConfigCreatedRef.current = true
      history.push(BILLING_STORE_CONFIGS.replace(':warehouse_id', warehouse_id).replace(':store_id', store_id))
    } catch (error) {
      isConfigCreatedRef.current = false

      // Only arm the dialog on the first attempt: re-arming it on the retry that already carried the
      // flag would cancel out with the caller closing it, leaving no feedback at all.
      if (!discardChannelPrices && requiresChannelPricesDiscard(error)) {
        setShowDiscardChannelPricesDialog(true)
      } else {
        // Channel price validations answer 422 with the offending channel, so show it verbatim.
        const validationMessage = error?.response?.status === 422 ? error?.response?.data?.error || error?.response?.data?.message : null

        setNotificationMessage({ show: true, type: 'error', title: validationMessage || t('billing.create_error_title') })
      }
    }

    setShowConfirmSaveDialog(false)
    setIsSaving(false)
  }

  const handleOnSave = async () => {
    const allStatuses = Object.values(tabsValidStatuses)

    const filteredStoreBillingServices = filterDeprecatedCategories(storeBillingServices)

    const areServicesWarehouseAndStoreValid =
      filteredStoreBillingServices?.length > 0 &&
      filteredStoreBillingServices.every(billingService => billingService.store_id == store_id && billingService.warehouse_id == warehouse_id)

    const areAllValid = allStatuses?.length > 0 && allStatuses.every(status => status === true) && areServicesWarehouseAndStoreValid

    if (!areAllValid) {
      setNotificationMessage({ show: true, type: 'error', title: t('billing.create_invalid_content_error_title') })
      setShowConfirmSaveDialog(false)
      return
    }

    await persistConfiguration()
  }

  const handleOnConfirmDiscardChannelPrices = async () => {
    await persistConfiguration(true)
    setShowDiscardChannelPricesDialog(false)
  }

  const handleOnUpdateBillingServices = newStoreBillingServices => {
    setStoreBillingServices(cloneDeep(newStoreBillingServices))
  }

  const handleOnUpdateBillingConfiguration = newStoreBillingConfiguration => {
    setStoreBillingConfiguration(cloneDeep(newStoreBillingConfiguration))
    // The API rejects a minimum fee on a configuration that isn't monthly.
    setStoreBillingServices(previousServices => previousServices && dropMinimumFeeUnlessMonthly(previousServices, newStoreBillingConfiguration.cutoff_interval))
  }

  const handleCopyFromOtherStore = () => {
    setShowCopyFromOtherStoreDialog(true)
  }

  const onConfirmCopyFromOtherStore = selectedBillingConfigId => {
    history.push(
      BILLING_STORE_CONFIG_DUPLICATE.replace(':warehouse_id', warehouse_id)
        .replace(':store_id', store_id)
        .replace(':billing_configuration_id', selectedBillingConfigId),
    )
    setShowCopyFromOtherStoreDialog(false)
    setCurrentTab(0)
    resetTabOptions()
    if (warehouse?.country === 'BR') {
      const additionalServices = data?.additional_billing_services || []
      setTabsValidStatuses(prevStatuses => ({
        ...prevStatuses,
        difal: false,
        shipping_insurance: false,
        ...(additionalServices.includes('SHIPPING_LABEL_NATURA_VENDA_DIRETA') ? { natura_shipping_label: false } : {}),
        ...(additionalServices.includes('SHIPPING_LABEL_NATURA_VENDA_DIRETA_ADDITIONAL_LABEL') ? { natura_additional_label: false } : {}),
        ...(additionalServices.includes('PICK_AND_PACK_NATURA_VENDA_DIRETA') ? { natura_pick_and_pack: false } : {}),
      }))

      setStoreBillingConfiguration(prevStatuses => ({
        ...prevStatuses,
        currency: warehouse?.country == 'MX' ? 'MXN' : 'BRL',
      }))
    }
  }

  return (
    <>
      <PageView
        topMenu={
          <PageTopBar marginClass="my-1">
            <div className="flex flex-row items-start">
              <Link
                to={BILLING_STORE_CONFIGS.replace(':warehouse_id', warehouse_id).replace(':store_id', store_id)}
                className="text-black hover:text-black mt-2"
              >
                <LeftOutlined className="mr-3" />
              </Link>
              <div className="flex flex-col">
                <label className="font-semibold text-gray200 text-lg">{t('billing.title')}</label>
                <label className="text-gray-300">{data?.name}</label>
              </div>
            </div>
            <div>
              <Button type={'primary'} disabled={false} onClick={() => handleCopyFromOtherStore()}>
                {t('billing.copy_config')}
              </Button>
            </div>
          </PageTopBar>
        }
        childrenFullWidth={true}
        topMenuFullWidth={true}
      >
        {isLoading || isLoadingData || isLoadingWarehouse ? (
          <>
            <Loader show={true}></Loader>
          </>
        ) : isError ? (
          <>Error: {error.message}</>
        ) : (
          <div className="flex rounded-lg bg-white overflow-hidden shadow w-full">
            <div className="w-1/6 p-4 border-r">
              <div className="flex flex-col">
                {tabOptions.length > 0 &&
                  tabOptions.map((option, index) => {
                    return (
                      <div key={index} className={`cursor-pointer mb-4 p-2`} onClick={() => handleOnTabChange(option, index)}>
                        {renderTabTitle(option, index)}
                      </div>
                    )
                  })}
              </div>
            </div>
            <div className="w-5/6 p-4">
              {tabOptions.length > 0 &&
                tabOptions.map((option, index) => {
                  return (
                    <div className="w-full" key={index}>
                      {currentTab === index && (
                        <BillingConfigTab
                          configKey={option.key}
                          onContinue={onContinue}
                          onUpdateBillingServices={handleOnUpdateBillingServices}
                          onUpdateBillingConfiguration={handleOnUpdateBillingConfiguration}
                          storeBillingServices={storeBillingServices}
                          storeBillingConfiguration={storeBillingConfiguration}
                          storeShippingPackages={storeShippingPackages}
                          warehouseLocationVolumeCategories={warehouseLocationVolumeCategories}
                          currentIndex={index}
                          lastIndex={tabOptions.length - 1}
                          onIsValid={handleOnIsValid}
                          storeId={store_id}
                          store={data}
                          warehouse={warehouse}
                        />
                      )}
                    </div>
                  )
                })}
            </div>
          </div>
        )}
      </PageView>

      <Notification
        show={notificationMessage.show}
        title={notificationMessage.title}
        type={notificationMessage.type}
        setShow={showNotification => setNotificationMessage({ ...notificationMessage, show: showNotification })}
      />

      <ConfirmDialog
        open={showConfirmSaveDialog}
        setOpen={setShowConfirmSaveDialog}
        title={t('billing.save_dialog.title')}
        description={t('billing.save_dialog.description')}
        confirmLabel={t('billing.save_dialog.confirm_button')}
        cancelLabel={t('billing.save_dialog.cancel_button')}
        onConfirm={handleOnSave}
        onCancel={() => setShowConfirmSaveDialog(false)}
        loading={isSaving}
      />

      <ConfirmDialog
        open={showDiscardChannelPricesDialog}
        setOpen={setShowDiscardChannelPricesDialog}
        title={t('billing.discard_channel_prices_dialog.title')}
        description={t('billing.discard_channel_prices_dialog.description')}
        confirmLabel={t('billing.discard_channel_prices_dialog.confirm_button')}
        cancelLabel={t('billing.discard_channel_prices_dialog.cancel_button')}
        onConfirm={handleOnConfirmDiscardChannelPrices}
        onCancel={() => setShowDiscardChannelPricesDialog(false)}
        loading={isSaving}
        type="danger"
      />

      <DuplicateConfigFromStoreModal open={showCopyFromOtherStoreDialog} setOpen={setShowCopyFromOtherStoreDialog} onConfirm={onConfirmCopyFromOtherStore} />
    </>
  )
}
